import { F, G } from '@mobily/ts-belt';
import logger from '@scale/frontend-lib/logging';
import { formatMaybeErrorForLogging } from '@scale/mm-shared';
import type { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import type { ChatStepTimerMetricEvent } from '@scale/shared/types/events/ChatStepTimerMetricEvent';
import { MutableRefObject, useEffect, useRef } from 'react';
import { TimerData, useTimer } from 'react-scale/components/scalers/withV2Timer';
import { Event, logEvent } from 'react-scale/lib/events';
import { StoreApi, createStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

type SubmissionTimeState = {
  startingTimestamp: number;
  startingServerTimeSpent: number;
};

/** props that require recreating ChatStepTimerStore if changed */
type ChatStepTimerProps = {
  assignmentId: string;
  projectId: string;
  taskTemplateVersion: number;
};

/** props that also initialize ChatStepTimerState */
type ChatStepTimerInitialState = {
  submissionTimeMetric: SubmissionTimeState;
};

export type ChatStepTimerState = ChatStepTimerInitialState & {
  logStepSubmission: (args: { stepId: string; serverTimeSpent: number }) => void;
};

/**
 * todo: need to tie this to the timer id somehow. we can't tell if the serverTimeSpent values we get should be treated
 * as the same session or not.
 */
const createChatStepTimerStore = (
  props: ChatStepTimerProps,
  initialState: ChatStepTimerInitialState,
): StoreApi<ChatStepTimerState> => {
  const { assignmentId, projectId, taskTemplateVersion } = props;
  const stateSessionStorageKey = `chat-step-timer-store-${projectId}-${assignmentId}-${taskTemplateVersion}`;

  return createStore<ChatStepTimerState>()(
    persist(
      (set, get) => ({
        ...initialState,
        logStepSubmission: async ({ stepId, serverTimeSpent }): Promise<void> => {
          const { submissionTimeMetric } = get();
          const { startingTimestamp, startingServerTimeSpent } = submissionTimeMetric;
          const timestampNow = Date.now();

          const event: ChatStepTimerMetricEvent = {
            projectId,
            assignmentId,
            taskTemplateVersion,
            stepId,

            startingTimestamp,
            endingTimestamp: timestampNow,

            startingServerTimeSpent,
            endingServerTimeSpent: serverTimeSpent,

            schemaVersion: 0,
          };

          try {
            await logEvent(Event.ChatStepTimerSubmissionMetric, event, { omitContext: true });
          } catch (error) {
            logger.error('ChatStepTimerStore: logStepSubmission', {
              error: formatMaybeErrorForLogging(error),
            });
          }

          set({
            submissionTimeMetric: {
              startingTimestamp: timestampNow,
              startingServerTimeSpent: serverTimeSpent,
            },
          });
        },
      }),
      {
        name: stateSessionStorageKey,
        storage: createJSONStorage(() => sessionStorage),
      },
    ),
  );
};

export type ChatStepTimerStore = StoreApi<ChatStepTimerState | undefined>;

type ChatStepTimerStoreRefValue = {
  store: ChatStepTimerStore;
  /** used to determine if the store needs to be recreated */
  storeProps: ChatStepTimerProps | undefined;
};

export type ChatStepTimerStoreRef = MutableRefObject<ChatStepTimerStoreRefValue>;

export const UNDEFINED_STORE = createStore<undefined>(() => undefined);

const CHAT_STEP_TIMER_STORE_REF_DEFAULT_VALUE: ChatStepTimerStoreRefValue = {
  store: UNDEFINED_STORE,
  storeProps: undefined,
};

const createChatStepTimerStoreRefValue = (
  getTimerData: () => TimerData,
  storeProps: ChatStepTimerProps | undefined,
): ChatStepTimerStoreRefValue =>
  storeProps === undefined
    ? CHAT_STEP_TIMER_STORE_REF_DEFAULT_VALUE
    : {
        store: createChatStepTimerStore(storeProps, {
          submissionTimeMetric: {
            startingTimestamp: Date.now(),
            startingServerTimeSpent: getTimerData().serverTimeSpent,
          },
        }),
        storeProps,
      };

/**
 * Creates a ChatStepTimerStore instance and wraps it into a reference.
 * That way we won't create a new store on each render.
 *
 * @example
 * const chatStepTimerStore = useCreateChatStepTimerStore({ ... });
 */
export const useCreateChatStepTimerStoreFromAssignment = (
  assignment: AssignmentJson | undefined,
): ChatStepTimerStoreRef => {
  const { assignmentId, projectId, taskTemplateVersion, isQueue } = assignment ?? {};
  const { getTimerData } = useTimer();

  const ref = useRef<ChatStepTimerStoreRefValue>(CHAT_STEP_TIMER_STORE_REF_DEFAULT_VALUE);
  // check if store needs to be recreated
  useEffect(() => {
    try {
      const expectedStoreProps =
        isQueue && assignmentId && projectId && G.isNotNullable(taskTemplateVersion)
          ? {
              assignmentId,
              projectId,
              taskTemplateVersion,
            }
          : undefined;

      if (!F.equals(ref.current.storeProps, expectedStoreProps)) {
        ref.current = createChatStepTimerStoreRefValue(getTimerData, expectedStoreProps);
      }
    } catch (error) {
      logger.error('useCreateChatStepTimerStoreFromAssignment', {
        error: formatMaybeErrorForLogging(error),
      });
    }
  }, [assignmentId, projectId, taskTemplateVersion, isQueue, getTimerData]);

  return ref;
};
